import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUploadPolling } from '../contexts/UploadPollingContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Label from '../components/ui/Label';
import Input from '../components/ui/Input';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import ErrorDialog from '../components/ui/ErrorDialog';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import FileDropzone from '../components/ui/FileDropzone';
import { toast } from 'react-hot-toast';
import { api } from '../services/authService';
import { organizationsService } from '../services/organizationsService';
import { ChevronRight, ChevronDown } from 'lucide-react';

const UPLOAD_TYPES = { universe: 'Universe', fast_id: 'Fast-ID', bulk: 'Bulk' };
/** Values for Upload History filter (independent of the main Upload type control). */
const UPLOAD_HISTORY_TYPE_FILTER = { all: 'all', universe: 'universe', fast_id: 'fast_id', bulk: 'bulk' };
// Max file size for universe/bulk file uploads (2 GB). File mode: presign → browser POST to S3 → `*_s3` API. For very large files or CORS issues, use the S3 path field and `upload_*_s3`.
const MAX_UPLOAD_FILE_BYTES = 2 * 1024 * 1024 * 1024;
// Allowed extensions for Bulk upload (lowercase)
const BULK_ALLOWED_EXTENSIONS = ['.json', '.csv'];
// Schema fields for Bulk column mapping (order preserved)
const BULK_SCHEMA_FIELDS = [
  'email',
  'email_md5',
  'first_name',
  'last_name',
  'gender',
  'dob',
  'phone_number',
  'optin_ip_address',
  'optin_site',
  'address',
  'city',
  'state',
  'zipcode',
  'optin_date',
];
// Bulk column mapping: these must stay checked and mapped (user cannot disable)
const BULK_ALWAYS_ENABLED_FIELDS = new Set(['email', 'optin_ip_address', 'optin_site', 'optin_date']);
/** Default checkbox state: required fields on, optional fields off (`bulkFieldEnabled[field] === true` means checked). */
const defaultBulkFieldEnabled = () =>
  BULK_SCHEMA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: BULK_ALWAYS_ENABLED_FIELDS.has(f) }), {});
// File Information fields (stored in bulk_file_upload via file_upload; text inputs in File Information section)
const BULK_METADATA_FIELDS = [
  'supplier_name',
  'purchase_date',
  'quality',
  'domains',
  'cost',
  'agreement',
];
const BULK_METADATA_FIELD_LABELS = {
  supplier_name: 'Supplier name',
  purchase_date: 'Purchase date',
  quality: 'Quality',
  domains: 'Domains',
  cost: 'Cost',
  agreement: 'Agreement',
};
/** Column mapping keys in display order (Bulk upload details dialog). */
const BULK_COLUMN_DETAIL_KEYS = [
  'email_field',
  'email_md5_field',
  'first_name_field',
  'last_name_field',
  'gender_field',
  'dob_field',
  'phone_number_field',
  'optin_ip_address_field',
  'optin_site_field',
  'address_field',
  'city_field',
  'state_field',
  'zipcode_field',
  'optin_date_field',
];

const formatBulkDetailValue = (val) => {
  if (val == null || val === '') return '—';
  if (typeof val === 'number') return String(val);
  const s = String(val).trim();
  return s || '—';
};
// Labels for bulk upload optional fields shown in Upload History expanded row (column mappings + metadata)
const BULK_EXPANDED_FIELD_LABELS = {
  email_field: 'Email column',
  email_md5_field: 'Email MD5 column',
  first_name_field: 'First name column',
  last_name_field: 'Last name column',
  gender_field: 'Gender column',
  dob_field: 'DOB column',
  phone_number_field: 'Phone number column',
  optin_ip_address_field: 'Opt-in IP address column',
  optin_site_field: 'Opt-in site column',
  address_field: 'Address column',
  city_field: 'City column',
  state_field: 'State column',
  zipcode_field: 'Zipcode column',
  optin_date_field: 'Opt-in date column',
  supplier_name: 'Supplier name',
  purchase_date: 'Purchase date',
  quality: 'Quality',
  domains: 'Domains',
  cost: 'Cost',
  agreement: 'Agreement',
};
// Optional bulk mappings: no first-row format validation (parity with validate_bulk.py OPTIONAL_FIELDS).
const BULK_OPTIONAL_FIELDS = new Set(['email_md5', 'first_name', 'last_name', 'gender', 'dob', 'phone_number', 'address', 'city', 'state', 'zipcode']);
// Display names for bulk schema fields (labels in the column mapping UI)
const BULK_SCHEMA_FIELD_LABELS = {
  email: 'Email',
  email_md5: 'Email MD5',
  first_name: 'First name',
  last_name: 'Last name',
  gender: 'Gender',
  dob: 'Date of birth',
  phone_number: 'Phone number',
  optin_ip_address: 'Opt-in IP address',
  optin_site: 'Opt-in site',
  address: 'Address',
  city: 'City',
  state: 'State',
  zipcode: 'Zipcode',
  optin_date: 'Opt-in date',
};
/** Aligned with ``validate_bulk.py`` / ``ipaddress.ip_address`` (Python). */
const isValidClientIpString = (s) => {
  let t = String(s).trim();
  if (!t) return false;
  if (t.includes('%') && t.includes(':')) {
    t = t.split('%')[0].trim();
  }
  if (t.includes(':')) {
    try {
      new URL(`http://[${t}]/`);
      return true;
    } catch {
      return false;
    }
  }
  if (!/^(?:\d{1,3}\.){3}\d{1,3}$/.test(t)) return false;
  return t.split('.').map(Number).every((n) => n >= 0 && n <= 255);
};

// Validation rules for bulk first row: field -> { test: regex or function, message: string }
const BULK_FIRST_ROW_VALIDATORS = {
  email: {
    test: (v) =>
      /^[A-Za-z0-9_%+'-][A-Za-z0-9._%+'-]*@[-A-Za-z0-9][A-Za-z0-9.-]*\.[A-Za-z]{2,}$/.test(v),
    message: 'Must be a valid email address.',
  },
  optin_ip_address: {
    test: (v) => isValidClientIpString(v),
    message: 'Must be a valid IPv4 or IPv6 address.',
  },
  optin_site: {
    test: (v) => String(v).trim().length > 0,
    message: 'Must be non-empty.',
  },
  optin_date: {
    test: (v) => !Number.isNaN(Date.parse(String(v).trim())),
    message: 'Must be a valid date/timestamp.',
  },
};
// FastCube has no universe table; universe upload is disabled for this org.
const FASTCUBE_ORG = 'FastCube';

/** 1–40 for validate_universe email_md5_column widget */
const UNIVERSE_COLUMN_OPTIONS = Array.from({ length: 40 }, (_, i) => String(i + 1));

const FileUpload = () => {
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const { startUploadJobPolling, openProcessingReport, reportModalOpen } = useUploadPolling();
  const [uploadType, setUploadType] = useState('universe');
  const [uploading, setUploading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // API Key state
  const [useApiKey, setUseApiKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [uploadResult, setUploadResult] = useState(null);

  // Upload history (file_upload / bulk_file_upload, last 10 per API; merged when filter is All)
  const [uploadHistory, setUploadHistory] = useState([]);
  const [loadingUploadHistory, setLoadingUploadHistory] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [uploadHistoryTypeFilter, setUploadHistoryTypeFilter] = useState(UPLOAD_HISTORY_TYPE_FILTER.all);
  const [bulkDetailsRow, setBulkDetailsRow] = useState(null);

  // Comma-separated universe hashes (paste and submit)
  const [universeHashesText, setUniverseHashesText] = useState('');
  // Existing S3 URI (universe input mode s3)
  const [universeS3Path, setUniverseS3Path] = useState('');
  // Universe input mode: 'file' (CSV), 'text' (paste hashes), or 's3' (existing S3 URI). Default File.
  const [universeInputMode, setUniverseInputMode] = useState('file');
  // Universe file mode: file chosen in dropzone, then column/format, then Upload
  const [selectedUniverseFile, setSelectedUniverseFile] = useState(null);
  const [universeEmailMd5Column, setUniverseEmailMd5Column] = useState('1');
  const [universeEmailFormat, setUniverseEmailFormat] = useState('md5');
  /** If true, Databricks update_universe drops and recreates the org universe table for this deployment before MERGE. */
  const [universeReplace, setUniverseReplace] = useState(false);
  const [showUniverseReplaceConfirm, setShowUniverseReplaceConfirm] = useState(false);
  const universeReplacePendingRunRef = useRef(null);

  const closeUniverseReplaceConfirm = () => {
    setShowUniverseReplaceConfirm(false);
    universeReplacePendingRunRef.current = null;
  };

  const confirmUniverseReplace = () => {
    const run = universeReplacePendingRunRef.current;
    universeReplacePendingRunRef.current = null;
    setShowUniverseReplaceConfirm(false);
    if (run) void run();
  };

  /** After universe validations pass: confirm drop/reload when checked, else run immediately. */
  const beginUniverseReplaceOrUpload = (performUpload) => {
    if (universeReplace) {
      universeReplacePendingRunRef.current = performUpload;
      setShowUniverseReplaceConfirm(true);
    } else {
      void performUpload();
    }
  };

  // Fast-ID: same input modes and fields as Universe (separate state so switching types does not clobber)
  const [fastIdInputMode, setFastIdInputMode] = useState('file');
  const [fastIdHashesText, setFastIdHashesText] = useState('');
  const [fastIdS3Path, setFastIdS3Path] = useState('');
  const [selectedFastIdFile, setSelectedFastIdFile] = useState(null);
  const [fastIdEmailMd5Column, setFastIdEmailMd5Column] = useState('1');
  const [fastIdEmailFormat, setFastIdEmailFormat] = useState('md5');

  // Bulk: selected file and column mapping (after file is chosen)
  const [selectedBulkFile, setSelectedBulkFile] = useState(null);
  const [bulkFileColumns, setBulkFileColumns] = useState([]);
  const [bulkColumnMapping, setBulkColumnMapping] = useState({});
  const [bulkFieldEnabled, setBulkFieldEnabled] = useState({});
  const [bulkMetadata, setBulkMetadata] = useState(
    BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {})
  );
  const [bulkFileParsing, setBulkFileParsing] = useState(false);
  const [bulkFirstRow, setBulkFirstRow] = useState(null);
  /** CSV without header: first-row cells; shown as c0: … | c1: … (labels bold). */
  const [bulkFirstLinePreview, setBulkFirstLinePreview] = useState(null);
  // Bulk (FastCube admin): file upload vs existing S3 object (same mappings as file; column names typed for S3).
  const [bulkInputMode, setBulkInputMode] = useState('file');
  const [bulkS3Path, setBulkS3Path] = useState('');
  const [bulkHasHeader, setBulkHasHeader] = useState(true);
  /** Same as bulkHasHeader; updated synchronously on toggle so file parsing matches the checkbox before React re-renders. */
  const bulkHasHeaderRef = useRef(bulkHasHeader);
  useEffect(() => {
    bulkHasHeaderRef.current = bulkHasHeader;
  }, [bulkHasHeader]);

  useEffect(() => {
    const loadOrganizations = async () => {
      setOrganizationsLoading(true);
      try {
        // Load from cookie first, then fetch from API if needed
        const orgs = await organizationsService.getOrganizations();
        setOrganizations(orgs);
        
        // Load selected organization from cookie, or default to first
        const savedSelected = organizationsService.getSelectedOrganization('name');
        if (savedSelected && orgs.some(o => o.name === savedSelected)) {
          setSelectedOrganization(savedSelected);
          organizationsService.setSelectedOrganization(savedSelected, 'name', orgs);
          if (savedSelected === FASTCUBE_ORG) {
            if (currentUser?.is_admin) setUploadType('bulk');
            else setUploadType('fast_id');
          }
        } else if (orgs.length > 0) {
          const defaultOrg = orgs[0].name;
          setSelectedOrganization(defaultOrg);
          organizationsService.setSelectedOrganization(defaultOrg, 'name', orgs);
          if (defaultOrg === FASTCUBE_ORG) {
            if (currentUser?.is_admin) setUploadType('bulk');
            else setUploadType('fast_id');
          }
        }
      } catch (error) {
        setErrorMessage('Failed to load organizations. Please refresh the page and try again.');
        setShowErrorDialog(true);
        console.error('Error loading organizations:', error);
      } finally {
        setOrganizationsLoading(false);
      }
    };

    loadOrganizations();
  }, [currentUser?.is_admin]);

  // When FastCube + admin, universe is invalid (no universe table); coerce to bulk. Do not override Fast-ID — admins may choose it explicitly.
  useEffect(() => {
    if (
      currentUser?.is_admin &&
      selectedOrganization === FASTCUBE_ORG &&
      uploadType === 'universe'
    ) {
      setUploadType('bulk');
    }
  }, [currentUser?.is_admin, selectedOrganization, uploadType]);

  // Upload History filter: reset to All when org changes (FastCube adds Bulk in the dropdown only).
  useEffect(() => {
    setUploadHistoryTypeFilter(UPLOAD_HISTORY_TYPE_FILTER.all);
    setExpandedHistoryId(null);
  }, [selectedOrganization]);

  const loadUploadHistoryRef = React.useRef(0);

  const getHistoryRowKey = (row) => `${row.type || 'row'}-${row.id}`;

  /** API stores `fast_id`; show human-readable label in the Type column. */
  const formatUploadHistoryType = (t) => {
    if (!t) return '—';
    if (t === 'fast_id') return 'fast-id';
    return t;
  };

  const loadUploadHistory = useCallback(async () => {
    if (!currentUser) return;
    const generation = loadUploadHistoryRef.current + 1;
    loadUploadHistoryRef.current = generation;
    setLoadingUploadHistory(true);
    try {
      // History is scoped to the current user only; each row includes organization_name for all orgs.
      const isFastCubeOrg = selectedOrganization === FASTCUBE_ORG;
      if (uploadHistoryTypeFilter === UPLOAD_HISTORY_TYPE_FILTER.all) {
        if (isFastCubeOrg) {
          const [universeRes, bulkRes] = await Promise.all([
            api.get('/api/v2/file_uploads'),
            api.get('/api/v2/bulk_file_uploads'),
          ]);
          if (loadUploadHistoryRef.current !== generation) return;
          const universeRows = Array.isArray(universeRes.data) ? universeRes.data : [];
          const bulkRows = Array.isArray(bulkRes.data) ? bulkRes.data : [];
          const merged = [...universeRows, ...bulkRows].sort((a, b) => {
            const ta = a.upload_time ? new Date(a.upload_time).getTime() : 0;
            const tb = b.upload_time ? new Date(b.upload_time).getTime() : 0;
            return tb - ta;
          });
          setUploadHistory(merged);
        } else {
          const res = await api.get('/api/v2/file_uploads');
          if (loadUploadHistoryRef.current !== generation) return;
          const rows = Array.isArray(res.data) ? res.data : [];
          setUploadHistory(rows);
        }
      } else if (uploadHistoryTypeFilter === UPLOAD_HISTORY_TYPE_FILTER.bulk) {
        const res = await api.get('/api/v2/bulk_file_uploads');
        if (loadUploadHistoryRef.current !== generation) return;
        setUploadHistory(Array.isArray(res.data) ? res.data : []);
      } else {
        const res = await api.get('/api/v2/file_uploads');
        if (loadUploadHistoryRef.current !== generation) return;
        let rows = Array.isArray(res.data) ? res.data : [];
        if (uploadHistoryTypeFilter === UPLOAD_HISTORY_TYPE_FILTER.universe) {
          rows = rows.filter((r) => r.type === 'universe');
        } else if (uploadHistoryTypeFilter === UPLOAD_HISTORY_TYPE_FILTER.fast_id) {
          rows = rows.filter((r) => r.type === 'fast_id');
        }
        setUploadHistory(rows);
      }
    } catch (error) {
      if (loadUploadHistoryRef.current !== generation) return;
      console.error('Upload history error:', error);
      setUploadHistory([]);
    } finally {
      if (loadUploadHistoryRef.current === generation) setLoadingUploadHistory(false);
    }
  }, [currentUser, uploadHistoryTypeFilter, selectedOrganization]);

  useEffect(() => {
    loadUploadHistory();
  }, [loadUploadHistory]);

  useEffect(() => {
    if (reportModalOpen && location.pathname === '/file-upload') {
      loadUploadHistory();
    }
  }, [reportModalOpen, location.pathname, loadUploadHistory]);

  // When FastCube is selected and user is not admin, only Fast-ID is allowed (not bulk or universe)
  useEffect(() => {
    if (
      selectedOrganization === FASTCUBE_ORG &&
      !currentUser?.is_admin &&
      (uploadType === 'bulk' || uploadType === 'universe')
    ) {
      setUploadType('fast_id');
      setSelectedBulkFile(null);
      setBulkFileColumns([]);
      setBulkColumnMapping({});
      setBulkFieldEnabled({});
      setBulkMetadata(BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
      setBulkFirstRow(null);
      setBulkFirstLinePreview(null);
      setBulkInputMode('file');
      setBulkS3Path('');
    }
  }, [selectedOrganization, currentUser?.is_admin, uploadType]);

  const buildHeaders = async () => {
    const headers = { 'Content-Type': 'multipart/form-data' };
    if (useApiKey && apiKeyInput.trim()) {
      headers['Authorization'] = `apikey ${apiKeyInput.trim()}`;
    }
    return headers;
  };

  /**
   * Multipart form POST to S3 using presigned post policy (bypasses app CDN; browser talks to S3 only).
   * The `file` part must be last, per S3 form POST.
   * @param {string} postUrl
   * @param {Record<string, string>} formFields hidden fields from the presign response
   * @param {File} file
   * @returns {Promise<void>}
   */
  const S3_BROWSER_BLOCK_ERROR = 'S3_BROWSER_DIRECT_UPLOAD_FAILED';

  const postFileToS3PresignedPost = async (postUrl, formFields, file) => {
    const form = new FormData();
    Object.keys(formFields).forEach((k) => {
      form.append(k, formFields[k]);
    });
    form.append('file', file, file.name || 'upload');
    let res;
    try {
      res = await fetch(postUrl, {
        method: 'POST',
        body: form,
      });
    } catch (e) {
      const n = e?.name || '';
      const msg = String(e?.message || e || '');
      if (n === 'TypeError' || n === 'NetworkError' || /NetworkError|failed to fetch|load failed|fetch resource/i.test(msg)) {
        throw new Error(S3_BROWSER_BLOCK_ERROR);
      }
      throw e;
    }
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(
        `S3 upload failed (${res.status} ${res.statusText})${t ? `: ${t.slice(0, 300)}` : ''}`
      );
    }
  };

  /** @returns {Promise<{ post_url: string, form_fields: object, s3_path: string }>} */
  const postUploadPresign = async (apiPath, file) => {
    const headers = await buildHeaders();
    const formData = new FormData();
    formData.append('organization', selectedOrganization);
    formData.append('file_name', file.name || 'upload');
    if (file.type) {
      formData.append('content_type', file.type);
    }
    const res = await api.post(apiPath, formData, { headers });
    return res.data;
  };

  const s3CorsOrBlockedMessage =
    'The browser could not complete the direct upload to S3. Check CORS on *-cube-file-uploads: allow Methods POST, PUT, GET, HEAD, your exact page origin, and AllowedHeaders * (form POST needs OPTIONS preflight). Confirm the API AWS region matches the bucket. Or use the S3 path field if uploads must bypass the browser.';

  /** Extra hint when browser → S3 PUT fails (CORS, bad signature, etc.). */
  const formatS3DirectUploadError = (error) => {
    if (!error) return null;
    const m = String(error.message || '');
    if (m === S3_BROWSER_BLOCK_ERROR) {
      return s3CorsOrBlockedMessage;
    }
    if (m.startsWith('S3 upload failed') || m.includes('S3 upload failed (')) {
      return `${m} Check S3 CORS (POST to bucket), bucket policy, and IAM; or use the S3 path field.`;
    }
    // Raw fetch failures we did not re-wrap (e.g. older code paths)
    if (
      error.name === 'NetworkError' ||
      (error.name === 'TypeError' &&
        (m === 'Failed to fetch' || m.toLowerCase().includes('load failed'))) ||
      /NetworkError when attempting to fetch|failed to fetch/i.test(m)
    ) {
      return s3CorsOrBlockedMessage;
    }
    return null;
  };

  const handleFileDropzoneReject = useCallback((message) => {
    setErrorMessage(message);
    setShowErrorDialog(true);
  }, []);

  const handleUniverseHashesSubmit = async () => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before submitting.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    const trimmed = (universeHashesText || '').trim();
    if (!trimmed) {
      setErrorMessage(
        universeEmailFormat === 'email'
          ? 'Please enter at least one email address (comma-separated).'
          : 'Please enter at least one MD5 hash (comma-separated).'
      );
      setShowErrorDialog(true);
      return;
    }
    if (selectedOrganization === FASTCUBE_ORG) {
      setErrorMessage('Universe upload is not available for FastCube (no universe table).');
      setShowErrorDialog(true);
      return;
    }
    const performUpload = async () => {
      setUploading(true);
      setUploadResult(null);
      try {
        const headers = await buildHeaders();
        const formData = new FormData();
        formData.append('hashes', trimmed);
        formData.append('organization', selectedOrganization);
        formData.append('email_format', universeEmailFormat);
        formData.append('replace', universeReplace ? 'true' : 'false');
        const res = await api.post('/api/v2/upload_universe_hashes', formData, { headers });
        const data = res.data;
        setUploadResult(data);
        if (data.execution_id) startUploadJobPolling(data.execution_id);
        toast.success('Universe hashes upload started. Check upload history below.');
        setUniverseHashesText('');
        loadUploadHistory();
      } catch (error) {
        console.error('Universe hashes upload error:', error);
        setErrorMessage(error.response?.data?.detail || error.message || 'Failed to upload universe hashes.');
        setShowErrorDialog(true);
      } finally {
        setUploading(false);
      }
    };
    beginUniverseReplaceOrUpload(performUpload);
  };

  const handleUniverseS3Submit = async () => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before submitting.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    const trimmed = (universeS3Path || '').trim();
    if (!trimmed) {
      setErrorMessage('Please enter an S3 path.');
      setShowErrorDialog(true);
      return;
    }
    if (selectedOrganization === FASTCUBE_ORG) {
      setErrorMessage('Universe upload is not available for FastCube (no universe table).');
      setShowErrorDialog(true);
      return;
    }
    const performUpload = async () => {
      setUploading(true);
      setUploadResult(null);
      try {
        const headers = await buildHeaders();
        const formData = new FormData();
        formData.append('s3_path', trimmed);
        formData.append('organization', selectedOrganization);
        formData.append('email_md5_column', universeEmailMd5Column);
        formData.append('email_format', universeEmailFormat);
        formData.append('replace', universeReplace ? 'true' : 'false');
        const res = await api.post('/api/v2/upload_universe_s3', formData, { headers });
        const data = res.data;
        setUploading(false);
        setUploadResult(data);
        if (data.execution_id) startUploadJobPolling(data.execution_id);
        toast.success('Universe upload queued from S3 (copy may run in the background). Check upload history below.');
        loadUploadHistory();
      } catch (error) {
        console.error('Universe S3 path error:', error);
        setErrorMessage(error.response?.data?.detail || error.message || 'Failed to start universe upload from S3.');
        setShowErrorDialog(true);
      } finally {
        setUploading(false);
      }
    };
    beginUniverseReplaceOrUpload(performUpload);
  };

  const handleFastIdHashesSubmit = async () => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before submitting.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    const trimmed = (fastIdHashesText || '').trim();
    if (!trimmed) {
      setErrorMessage(
        fastIdEmailFormat === 'email'
          ? 'Please enter at least one email address (comma-separated).'
          : 'Please enter at least one MD5 hash (comma-separated).'
      );
      setShowErrorDialog(true);
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const headers = await buildHeaders();
      const formData = new FormData();
      formData.append('hashes', trimmed);
      formData.append('organization', selectedOrganization);
      formData.append('email_format', fastIdEmailFormat);
      const res = await api.post('/api/v2/upload_fast_id_hashes', formData, { headers });
      const data = res.data;
      setUploadResult(data);
      if (data.execution_id) startUploadJobPolling(data.execution_id, 'fast_id');
      toast.success('Fast-ID hashes upload started. Check upload history below.');
      setFastIdHashesText('');
      loadUploadHistory();
    } catch (error) {
      console.error('Fast-ID hashes upload error:', error);
      setErrorMessage(error.response?.data?.detail || error.message || 'Failed to upload Fast-ID hashes.');
      setShowErrorDialog(true);
    } finally {
      setUploading(false);
    }
  };

  const handleFastIdS3Submit = async () => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before submitting.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    const trimmed = (fastIdS3Path || '').trim();
    if (!trimmed) {
      setErrorMessage('Please enter an S3 path.');
      setShowErrorDialog(true);
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const headers = await buildHeaders();
      const formData = new FormData();
      formData.append('s3_path', trimmed);
      formData.append('organization', selectedOrganization);
      formData.append('email_md5_column', fastIdEmailMd5Column);
      formData.append('email_format', fastIdEmailFormat);
      const res = await api.post('/api/v2/upload_fast_id_s3', formData, { headers });
      const data = res.data;
      setUploading(false);
      setUploadResult(data);
      if (data.execution_id) startUploadJobPolling(data.execution_id, 'fast_id');
      toast.success('Fast-ID upload queued from S3 (copy may run in the background). Check upload history below.');
      loadUploadHistory();
    } catch (error) {
      console.error('Fast-ID S3 path error:', error);
      setErrorMessage(error.response?.data?.detail || error.message || 'Failed to start Fast-ID upload from S3.');
      setShowErrorDialog(true);
    } finally {
      setUploading(false);
    }
  };

  const handleBulkS3Submit = async () => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before submitting.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    if (selectedOrganization !== FASTCUBE_ORG || !currentUser?.is_admin) {
      setErrorMessage('Bulk S3 upload is only available for FastCube organization admins.');
      setShowErrorDialog(true);
      return;
    }
    const trimmedPath = (bulkS3Path || '').trim();
    if (!trimmedPath) {
      setErrorMessage('Please enter an S3 path.');
      setShowErrorDialog(true);
      return;
    }
    const requiredBulkMappings = ['email', 'optin_ip_address', 'optin_site', 'optin_date'];
    const missingMappings = requiredBulkMappings.filter((field) => {
      const v = bulkColumnMapping[field];
      return v == null || String(v).trim() === '';
    });
    if (missingMappings.length > 0) {
      const labels = missingMappings.map((f) => BULK_SCHEMA_FIELD_LABELS[f] ?? f);
      setErrorMessage(
        missingMappings.length === 1
          ? `Column mapping required: enter the CSV column name for ${labels[0]}.`
          : `Column mapping required: enter CSV column names for ${labels.join(', ')}.`
      );
      setShowErrorDialog(true);
      return;
    }
    const missingFileInfo = BULK_METADATA_FIELDS.filter((key) => {
      const val = bulkMetadata[key];
      return val == null || String(val).trim() === '';
    });
    if (missingFileInfo.length > 0) {
      const labels = missingFileInfo.map((key) => BULK_METADATA_FIELD_LABELS[key] ?? key);
      setErrorMessage(
        'File Information required: please provide a value for ' +
          (labels.length === 1 ? labels[0] : labels.slice(0, -1).join(', ') + ', and ' + labels[labels.length - 1]) +
          '.'
      );
      setShowErrorDialog(true);
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const headers = await buildHeaders();
      const formData = new FormData();
      formData.append('s3_path', trimmedPath);
      formData.append('organization', selectedOrganization);
      formData.append('has_header', bulkHasHeader ? 'true' : 'false');
      BULK_SCHEMA_FIELDS.forEach((field) => {
        const active =
          BULK_ALWAYS_ENABLED_FIELDS.has(field) || bulkFieldEnabled[field] === true;
        const value = active && bulkColumnMapping[field] ? bulkColumnMapping[field] : '';
        formData.append(`${field}_field`, value);
      });
      BULK_METADATA_FIELDS.forEach((key) => {
        const val = bulkMetadata[key];
        formData.append(key, val != null && String(val).trim() !== '' ? String(val).trim() : '');
      });
      const res = await api.post('/api/v2/upload_bulk_s3', formData, { headers });
      const data = res.data;
      setUploading(false);
      setUploadResult(data);
      if (data.execution_id) startUploadJobPolling(data.execution_id);
      toast.success(data.message || 'Bulk upload queued from S3 (copy may run in the background). Check upload history below.');
      loadUploadHistory();
    } catch (error) {
      console.error('Bulk S3 upload error:', error);
      const detail = error.response?.data?.detail;
      const detailStr =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg ?? d).join(' ')
            : detail != null
              ? String(detail)
              : '';
      setErrorMessage(detailStr.trim() || error.message || 'Failed to start bulk upload from S3.');
      setShowErrorDialog(true);
    } finally {
      setUploading(false);
    }
  };

  const clearFastIdFileInput = () => {
    const fileInput = document.getElementById('uploadFileFastId');
    if (fileInput) fileInput.value = '';
    setSelectedFastIdFile(null);
  };

  const handleFastIdFileSelected = (file) => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before uploading a file.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    if (file.size > MAX_UPLOAD_FILE_BYTES) {
      setErrorMessage('Fast-ID file size must be 2 GB or less. For larger files, use Fast-ID → S3.');
      setShowErrorDialog(true);
      return;
    }
    setSelectedFastIdFile(file);
  };

  const handleFastIdUpload = async () => {
    const file = selectedFastIdFile;
    if (!file) return;
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before uploading a file.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    if (file.size > MAX_UPLOAD_FILE_BYTES) {
      setErrorMessage('Fast-ID file size must be 2 GB or less. For larger files, use Fast-ID → S3.');
      setShowErrorDialog(true);
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const { post_url, form_fields, s3_path } = await postUploadPresign(
        '/api/v2/upload_fast_id_presign',
        file
      );
      await postFileToS3PresignedPost(post_url, form_fields, file);
      const headers = await buildHeaders();
      const formData = new FormData();
      formData.append('s3_path', s3_path);
      formData.append('organization', selectedOrganization);
      formData.append('email_md5_column', fastIdEmailMd5Column);
      formData.append('email_format', fastIdEmailFormat);
      const res = await api.post('/api/v2/upload_fast_id_s3', formData, { headers });
      const data = res.data;
      setUploadResult(data);
      if (data.execution_id) startUploadJobPolling(data.execution_id, 'fast_id');
      toast.success('Fast-ID upload started. Check upload history below.');
      clearFastIdFileInput();
      loadUploadHistory();
    } catch (error) {
      console.error('Fast-ID upload error:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const detailStr =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg ?? d).join(' ')
            : detail != null
              ? String(detail)
              : '';
      let msg = detailStr.trim() || error.message || 'Failed to upload Fast-ID file.';
      const s3Hint = formatS3DirectUploadError(error);
      if (s3Hint) {
        msg = s3Hint;
      } else if (status === 413) {
        msg =
          'The upload was rejected (HTTP 413). A load balancer, CDN, or reverse proxy in front of the API often enforces a limit below 2 GB, even for smaller files. Use Fast-ID input → S3 and enter s3://bucket/path to your CSV instead.';
      } else if (!error.response) {
        if (error.code === 'ECONNABORTED' || String(error.message || '').toLowerCase().includes('timeout')) {
          msg =
            'The upload timed out or was cancelled. For large files, use Fast-ID input → S3 and enter s3://bucket/path to your CSV instead.';
        } else if (error.message === 'Network Error') {
          msg =
            'Network error while uploading. For large files, use Fast-ID input → S3 and enter s3://bucket/path to your CSV instead.';
        }
      }
      setErrorMessage(msg);
      setShowErrorDialog(true);
    } finally {
      setUploading(false);
    }
  };

  const clearUniverseFileInput = () => {
    const fileInput = document.getElementById('uploadFile');
    if (fileInput) fileInput.value = '';
    setSelectedUniverseFile(null);
  };

  const handleUniverseFileSelected = (file) => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before uploading a file.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    if (file.size > MAX_UPLOAD_FILE_BYTES) {
      setErrorMessage('Universe file size must be 2 GB or less. For larger files, use Universe → S3.');
      setShowErrorDialog(true);
      return;
    }
    setSelectedUniverseFile(file);
  };

  const handleUniverseUpload = async () => {
    const file = selectedUniverseFile;
    if (!file) return;
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before uploading a file.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    if (file.size > MAX_UPLOAD_FILE_BYTES) {
      setErrorMessage('Universe file size must be 2 GB or less. For larger files, use Universe → S3.');
      setShowErrorDialog(true);
      return;
    }
    const performUpload = async () => {
      setUploading(true);
      setUploadResult(null);
      try {
        const { post_url, form_fields, s3_path } = await postUploadPresign(
          '/api/v2/upload_universe_presign',
          file
        );
        await postFileToS3PresignedPost(post_url, form_fields, file);
        const headers = await buildHeaders();
        const formData = new FormData();
        formData.append('s3_path', s3_path);
        formData.append('organization', selectedOrganization);
        formData.append('email_md5_column', universeEmailMd5Column);
        formData.append('email_format', universeEmailFormat);
        formData.append('replace', universeReplace ? 'true' : 'false');
        const res = await api.post('/api/v2/upload_universe_s3', formData, { headers });
        const data = res.data;
        setUploadResult(data);
        if (data.execution_id) startUploadJobPolling(data.execution_id);
        toast.success('Universe upload started. Check upload history below.');
        clearUniverseFileInput();
        loadUploadHistory();
      } catch (error) {
        console.error('Universe upload error:', error);
        const status = error.response?.status;
        const detail = error.response?.data?.detail;
        const detailStr =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d?.msg ?? d).join(' ')
              : detail != null
                ? String(detail)
                : '';
        let msg = detailStr.trim() || error.message || 'Failed to upload universe file.';
        const s3Hint = formatS3DirectUploadError(error);
        if (s3Hint) {
          msg = s3Hint;
        } else if (status === 413) {
          msg =
            'The upload was rejected (HTTP 413). A load balancer, CDN, or reverse proxy in front of the API often enforces a limit below 2 GB, even for smaller files. Use Universe input → S3 and enter s3://bucket/path to your CSV instead.';
        } else if (!error.response) {
          if (error.code === 'ECONNABORTED' || String(error.message || '').toLowerCase().includes('timeout')) {
            msg =
              'The upload timed out or was cancelled. For large files, use Universe input → S3 and enter s3://bucket/path to your CSV instead.';
          } else if (error.message === 'Network Error') {
            msg =
              'Network error while uploading. For large files, use Universe input → S3 and enter s3://bucket/path to your CSV instead.';
          }
        }
        setErrorMessage(msg);
        setShowErrorDialog(true);
      } finally {
        setUploading(false);
      }
    };
    beginUniverseReplaceOrUpload(performUpload);
  };

  const handleFileUpload = (file) => {
    if (uploadType === 'universe') {
      handleUniverseFileSelected(file);
    } else if (uploadType === 'fast_id') {
      handleFastIdFileSelected(file);
    } else if (uploadType === 'bulk') {
      handleBulkFileSelected(file);
    }
  };

  /** Parse Bulk file to get column names; set state for mapping UI. */
  const handleBulkFileSelected = async (file) => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before uploading a file.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    const name = (file.name || '').toLowerCase();
    const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
    if (!BULK_ALLOWED_EXTENSIONS.includes(ext)) {
      setErrorMessage('Bulk file must be .json or .csv.');
      setShowErrorDialog(true);
      return;
    }
    if (file.size > MAX_UPLOAD_FILE_BYTES) {
      setErrorMessage('Bulk file size must be 2 GB or less. Split the file or reduce size before uploading.');
      setShowErrorDialog(true);
      return;
    }
    setBulkFileParsing(true);
    setSelectedBulkFile(null);
    setBulkFileColumns([]);
    setBulkColumnMapping({});
    setBulkFirstLinePreview(null);
    try {
      let columns = [];
      let firstRow = null;
      let firstLinePreview = null;
      const text = await file.text();
      if (ext === '.csv') {
        const hasHeader = bulkHasHeaderRef.current;
        const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        const firstDataRowIndex = hasHeader ? 1 : 0;
        const previewLine = lines[firstDataRowIndex];
        if (!hasHeader && previewLine !== undefined) {
          firstLinePreview = previewLine
            .split(',')
            .map((s) => s.trim().replace(/^"|"$/g, ''));
        }
        if (hasHeader) {
          const headerLine = lines[0] || '';
          columns = headerLine.split(',').map((h) => h.trim()).filter(Boolean);
        } else {
          const valueLine = lines[0] || '';
          const values = valueLine.split(',');
          columns = values.map((_, i) => `_c${i}`);
        }
        if (lines.length > firstDataRowIndex && columns.length > 0) {
          const valueLine = lines[firstDataRowIndex];
          const rawValues = valueLine.split(',').map((s) => s.trim().replace(/^"|"$/g, ''));
          firstRow = {};
          columns.forEach((col, i) => {
            firstRow[col] = rawValues[i] !== undefined ? String(rawValues[i]).trim() : '';
          });
        }
      } else {
        const data = JSON.parse(text);
        const first = Array.isArray(data) ? data[0] : data;
        if (first && typeof first === 'object' && !Array.isArray(first)) {
          columns = Object.keys(first);
          firstRow = {};
          columns.forEach((col) => {
            const v = first[col];
            firstRow[col] = v != null ? String(v).trim() : '';
          });
        }
      }
      setBulkFileColumns(columns);
      setBulkFirstRow(firstRow);
      setBulkFirstLinePreview(firstLinePreview);
      const defaultMapping = {};
      BULK_SCHEMA_FIELDS.forEach((field) => {
        defaultMapping[field] =
          BULK_ALWAYS_ENABLED_FIELDS.has(field) && columns.includes(field) ? field : '';
      });
      setBulkColumnMapping(defaultMapping);
      setBulkFieldEnabled(defaultBulkFieldEnabled());
      setSelectedBulkFile(file);
    } catch (e) {
      setErrorMessage('Could not read file columns. Check that the file is valid CSV (with header) or JSON.');
      setShowErrorDialog(true);
    } finally {
      setBulkFileParsing(false);
    }
  };

  /** Validate first row by current mapping; returns { field: errorMessage }. Only validates enabled, mapped fields. */
  const getBulkValidationErrors = () => {
    if (!bulkFirstRow || !bulkColumnMapping) return {};
    const errors = {};
    BULK_SCHEMA_FIELDS.forEach((field) => {
      if (!BULK_ALWAYS_ENABLED_FIELDS.has(field) && bulkFieldEnabled[field] !== true) return;
      const col = bulkColumnMapping[field];
      if (!col) return;
      if (BULK_OPTIONAL_FIELDS.has(field)) return;
      const value = bulkFirstRow[col];
      const str = value != null ? String(value).trim() : '';
      const rule = BULK_FIRST_ROW_VALIDATORS[field];
      if (str === '') {
        errors[field] = rule ? rule.message : 'Required.';
        return;
      }
      if (rule && !rule.test(str)) errors[field] = rule.message;
    });
    return errors;
  };

  const handleBulkUpload = async (file, columnMapping = {}) => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before uploading a file.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    const requiredBulkMappings = ['email', 'optin_ip_address', 'optin_site', 'optin_date'];
    const missingMappings = requiredBulkMappings.filter((field) => {
      const v = columnMapping[field];
      return v == null || String(v).trim() === '';
    });
    if (missingMappings.length > 0) {
      const labels = missingMappings.map((f) => BULK_SCHEMA_FIELD_LABELS[f] ?? f);
      setErrorMessage(
        missingMappings.length === 1
          ? `Column mapping required: select a file column for ${labels[0]} instead of leaving it on "Select column".`
          : `Column mapping required: select file columns for ${labels.join(', ')} instead of leaving any on "Select column".`
      );
      setShowErrorDialog(true);
      return;
    }
    const validationErrors = getBulkValidationErrors();
    if (Object.keys(validationErrors).length > 0) {
      const msg = 'First row validation failed: ' + Object.entries(validationErrors).map(([f, m]) => `${f}: ${m}`).join('; ');
      setErrorMessage(msg);
      setShowErrorDialog(true);
      return;
    }
    // File Information: each field must have a non-space value
    const missingFileInfo = BULK_METADATA_FIELDS.filter((key) => {
      const val = bulkMetadata[key];
      return val == null || String(val).trim() === '';
    });
    if (missingFileInfo.length > 0) {
      const labels = missingFileInfo.map((key) => BULK_METADATA_FIELD_LABELS[key] ?? key);
      setErrorMessage(
        'File Information required: please provide a value for ' +
          (labels.length === 1 ? labels[0] : labels.slice(0, -1).join(', ') + ', and ' + labels[labels.length - 1]) +
          '.'
      );
      setShowErrorDialog(true);
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const { post_url, form_fields, s3_path } = await postUploadPresign(
        '/api/v2/upload_bulk_presign',
        file
      );
      await postFileToS3PresignedPost(post_url, form_fields, file);
      const headers = await buildHeaders();
      const formData = new FormData();
      formData.append('s3_path', s3_path);
      formData.append('organization', selectedOrganization);
      formData.append('has_header', bulkHasHeader ? 'true' : 'false');
      // All mapping fields: send selected file column name for each schema field (empty if not mapped)
      BULK_SCHEMA_FIELDS.forEach((field) => {
        const active =
          BULK_ALWAYS_ENABLED_FIELDS.has(field) || bulkFieldEnabled[field] === true;
        const value = active && columnMapping[field] ? columnMapping[field] : '';
        formData.append(`${field}_field`, value);
      });
      // All metadata fields: send value or empty string
      BULK_METADATA_FIELDS.forEach((key) => {
        const val = bulkMetadata[key];
        formData.append(key, val != null && String(val).trim() !== '' ? String(val).trim() : '');
      });
      const res = await api.post('/api/v2/upload_bulk_s3', formData, { headers });
      const data = res.data;
      setUploadResult(data);
      if (data.execution_id) startUploadJobPolling(data.execution_id);
      toast.success(data.message || 'Bulk file uploaded successfully.');
      loadUploadHistory();
      const fileInput = document.getElementById('uploadFile');
      if (fileInput) fileInput.value = '';
      setSelectedBulkFile(null);
      setBulkFileColumns([]);
      setBulkColumnMapping({});
      setBulkFieldEnabled({});
      setBulkMetadata(BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
      setBulkFirstRow(null);
      setBulkFirstLinePreview(null);
    } catch (error) {
      console.error('Bulk upload error:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const detailStr =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg ?? d).join(' ')
            : detail != null
              ? String(detail)
              : '';
      let msg = detailStr.trim() || error.message || 'Failed to upload bulk file.';
      const s3Hint = formatS3DirectUploadError(error);
      if (s3Hint) {
        msg = s3Hint;
      } else if (status === 413) {
        msg =
          'The upload was rejected (HTTP 413). A load balancer, CDN, or reverse proxy in front of the API often enforces a limit below 2 GB, even when your file is under 2 GB. Use Bulk input → S3 with s3://bucket/path, split the file, or ask your team to raise the upload size limit in front of the API.';
      } else if (!error.response) {
        if (error.code === 'ECONNABORTED' || String(error.message || '').toLowerCase().includes('timeout')) {
          msg =
            'The upload timed out or was cancelled. Try a smaller file or split the data.';
        } else if (error.message === 'Network Error') {
          msg = 'Network error while uploading. Check your connection and try again.';
        }
      }
      setErrorMessage(msg);
      setShowErrorDialog(true);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="space-y-2">
            <Label htmlFor="organization">Organization</Label>
            {organizationsLoading ? (
              <p className="text-sm text-gray-500">Loading organizations...</p>
            ) : organizations.length === 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-amber-600">
                  No organizations available. Please contact an administrator to be added to an organization.
                </p>
                <FunctionalSelect
                  value=""
                  onValueChange={() => {}}
                  placeholder="No organizations available"
                  className="w-full"
                  disabled
                >
                </FunctionalSelect>
              </div>
            ) : (
              <FunctionalSelect
                value={organizationsService.getSelectedOptionValue(organizations, selectedOrganization, 'name')}
                onValueChange={(value) => {
                  const parsed = organizationsService.parseOrganizationOptionValue(value);
                  const name = parsed ? parsed.name : value;
                  if (parsed) {
                    organizationsService.setSelectedOrganization(name, 'name', [{ name: parsed.name, id: parsed.id }]);
                  } else {
                    organizationsService.setSelectedOrganization(name, 'name', organizations);
                  }
                  setSelectedOrganization(name);
                  setSelectedUniverseFile(null);
                  setSelectedFastIdFile(null);
                  if (name === FASTCUBE_ORG && currentUser?.is_admin) {
                    setUploadType('bulk');
                  } else if (name === FASTCUBE_ORG && !currentUser?.is_admin) {
                    setUploadType('fast_id');
                    setSelectedBulkFile(null);
                    setBulkFileColumns([]);
                    setBulkColumnMapping({});
                    setBulkFieldEnabled({});
                    setBulkMetadata(BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
                    setBulkFirstRow(null);
                    setBulkFirstLinePreview(null);
                    setBulkInputMode('file');
                    setBulkS3Path('');
                  } else if (uploadType === 'bulk') {
                    setUploadType('universe');
                    setSelectedBulkFile(null);
                    setBulkFileColumns([]);
                    setBulkColumnMapping({});
                    setBulkFieldEnabled({});
                    setBulkMetadata(BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
                    setBulkFirstRow(null);
                    setBulkFirstLinePreview(null);
                    setBulkInputMode('file');
                    setBulkS3Path('');
                  }
                }}
                placeholder="Select organization"
                className="w-full"
              >
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
                    {org.name}
                  </SelectItem>
                ))}
              </FunctionalSelect>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="uploadType">Upload type</Label>
            <FunctionalSelect
              id="uploadType"
              value={uploadType}
              onValueChange={(value) => {
                setUploadType(value);
                setUploadResult(null);
                if (value === 'bulk') {
                  setUniverseInputMode('file');
                  setUniverseS3Path('');
                  setSelectedUniverseFile(null);
                  setFastIdInputMode('file');
                  setFastIdS3Path('');
                  setSelectedFastIdFile(null);
                  setFastIdHashesText('');
                  setSelectedBulkFile(null);
                  setBulkFileColumns([]);
                  setBulkColumnMapping({});
                  setBulkFieldEnabled({});
                  setBulkMetadata(BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
                  setBulkFirstRow(null);
                  setBulkFirstLinePreview(null);
                  setBulkInputMode('file');
                  setBulkS3Path('');
                } else if (value === 'universe') {
                  setSelectedBulkFile(null);
                  setBulkFileColumns([]);
                  setBulkColumnMapping({});
                  setBulkFieldEnabled({});
                  setBulkMetadata(BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
                  setBulkFirstRow(null);
                  setBulkFirstLinePreview(null);
                  setBulkInputMode('file');
                  setBulkS3Path('');
                } else if (value === 'fast_id') {
                  setSelectedBulkFile(null);
                  setBulkFileColumns([]);
                  setBulkColumnMapping({});
                  setBulkFieldEnabled({});
                  setBulkMetadata(BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
                  setBulkFirstRow(null);
                  setBulkFirstLinePreview(null);
                  setBulkInputMode('file');
                  setBulkS3Path('');
                }
              }}
              placeholder="Select upload type"
              className="w-full"
              disabled={uploading}
            >
              {selectedOrganization !== FASTCUBE_ORG && (
                <SelectItem value="universe">{UPLOAD_TYPES.universe}</SelectItem>
              )}
              <SelectItem value="fast_id">{UPLOAD_TYPES.fast_id}</SelectItem>
              {selectedOrganization === FASTCUBE_ORG && currentUser?.is_admin && (
                <SelectItem value="bulk">{UPLOAD_TYPES.bulk}</SelectItem>
              )}
            </FunctionalSelect>
          </div>

          {uploadType === 'bulk' && (
            <div className="space-y-2">
              <Label>Bulk input</Label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="bulkInputMode"
                    value="file"
                    checked={bulkInputMode === 'file'}
                    onChange={() => {
                      setBulkInputMode('file');
                      setBulkS3Path('');
                    }}
                    disabled={uploading}
                    className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
                  />
                  <span className="text-sm font-medium">File</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="bulkInputMode"
                    value="s3"
                    checked={bulkInputMode === 's3'}
                    onChange={() => {
                      setBulkInputMode('s3');
                      setSelectedBulkFile(null);
                      setBulkFileColumns([]);
                      setBulkFirstRow(null);
                      setBulkFirstLinePreview(null);
                      setBulkColumnMapping(BULK_SCHEMA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
                      setBulkFieldEnabled(defaultBulkFieldEnabled());
                      const fileInput = document.getElementById('uploadFile');
                      if (fileInput) fileInput.value = '';
                    }}
                    disabled={uploading}
                    className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
                  />
                  <span className="text-sm font-medium">S3</span>
                </label>
              </div>
              <div className="flex items-center space-x-2 pt-1">
                <input
                  type="checkbox"
                  id="bulkHasHeader"
                  checked={bulkHasHeader}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    bulkHasHeaderRef.current = checked;
                    setBulkHasHeader(checked);
                    if (bulkInputMode === 'file') {
                      setSelectedBulkFile(null);
                      setBulkFileColumns([]);
                      setBulkColumnMapping({});
                      setBulkFieldEnabled({});
                      setBulkFirstRow(null);
                      setBulkFirstLinePreview(null);
                      const fileInput = document.getElementById('uploadFile');
                      if (fileInput) fileInput.value = '';
                    }
                  }}
                  disabled={uploading}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <Label htmlFor="bulkHasHeader" className="cursor-pointer">
                  Has Header
                </Label>
              </div>
            </div>
          )}

          {uploadType === 'universe' && (
            <div className="space-y-2">
              <Label>Universe input</Label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="universeInputMode"
                    value="file"
                    checked={universeInputMode === 'file'}
                    onChange={() => {
                      setUniverseInputMode('file');
                      setSelectedUniverseFile(null);
                    }}
                    disabled={uploading}
                    className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
                  />
                  <span className="text-sm font-medium">File</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="universeInputMode"
                    value="text"
                    checked={universeInputMode === 'text'}
                    onChange={() => {
                      setUniverseInputMode('text');
                      setSelectedUniverseFile(null);
                    }}
                    disabled={uploading}
                    className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
                  />
                  <span className="text-sm font-medium">Text</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="universeInputMode"
                    value="s3"
                    checked={universeInputMode === 's3'}
                    onChange={() => {
                      setUniverseInputMode('s3');
                      setSelectedUniverseFile(null);
                    }}
                    disabled={uploading}
                    className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
                  />
                  <span className="text-sm font-medium">S3</span>
                </label>
              </div>
              <div className="flex items-center space-x-2 pt-1">
                <input
                  type="checkbox"
                  id="universeReplace"
                  checked={universeReplace}
                  onChange={(e) => setUniverseReplace(e.target.checked)}
                  disabled={uploading}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <Label htmlFor="universeReplace" className="cursor-pointer">
                  Replace universe table (drop and reload)
                </Label>
              </div>
            </div>
          )}

          {uploadType === 'fast_id' && (
            <div className="space-y-2">
              <Label>Fast-ID input</Label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="fastIdInputMode"
                    value="file"
                    checked={fastIdInputMode === 'file'}
                    onChange={() => {
                      setFastIdInputMode('file');
                      setSelectedFastIdFile(null);
                    }}
                    disabled={uploading}
                    className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
                  />
                  <span className="text-sm font-medium">File</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="fastIdInputMode"
                    value="text"
                    checked={fastIdInputMode === 'text'}
                    onChange={() => {
                      setFastIdInputMode('text');
                      setSelectedFastIdFile(null);
                    }}
                    disabled={uploading}
                    className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
                  />
                  <span className="text-sm font-medium">Text</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="fastIdInputMode"
                    value="s3"
                    checked={fastIdInputMode === 's3'}
                    onChange={() => {
                      setFastIdInputMode('s3');
                      setSelectedFastIdFile(null);
                    }}
                    disabled={uploading}
                    className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
                  />
                  <span className="text-sm font-medium">S3</span>
                </label>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="useApiKey"
                checked={useApiKey}
                onChange={(e) => {
                  setUseApiKey(e.target.checked);
                  if (!e.target.checked) {
                    setApiKeyInput('');
                  }
                }}
                disabled={uploading}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <Label htmlFor="useApiKey" className="cursor-pointer">
                Use API Key
              </Label>
            </div>
          </div>

          {useApiKey && (
            <div className="space-y-2">
              <Label htmlFor="apiKeyInput">API Key</Label>
              <Input
                id="apiKeyInput"
                type="password"
                autoComplete="off"
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                placeholder="Enter API key"
                disabled={uploading}
              />
            </div>
          )}

          {((uploadType === 'bulk' && bulkInputMode === 'file') ||
            (uploadType === 'universe' && universeInputMode === 'file') ||
            (uploadType === 'fast_id' && fastIdInputMode === 'file')) && (
            <div className="space-y-2">
              <FileDropzone
                inputId={uploadType === 'fast_id' ? 'uploadFileFastId' : 'uploadFile'}
                label={
                  uploadType === 'universe'
                    ? 'Select Universe CSV'
                    : uploadType === 'fast_id'
                      ? 'Select Fast-ID CSV'
                      : 'Select Bulk File'
                }
                accept={uploadType === 'bulk' ? '.csv,.json' : '.csv'}
                maxSize={
                  (uploadType === 'bulk' && bulkInputMode === 'file') ||
                  (uploadType === 'universe' && universeInputMode === 'file') ||
                  (uploadType === 'fast_id' && fastIdInputMode === 'file')
                    ? MAX_UPLOAD_FILE_BYTES
                    : undefined
                }
                tooLargeHint={
                  uploadType === 'universe'
                    ? 'Use Universe input → S3 and enter s3://bucket/path to your CSV instead.'
                    : uploadType === 'fast_id'
                      ? 'Use Fast-ID input → S3 and enter s3://bucket/path to your CSV instead.'
                      : 'Split the file or reduce size before uploading.'
                }
                onReject={handleFileDropzoneReject}
                disabled={
                  uploading ||
                  !selectedOrganization ||
                  organizationsLoading ||
                  organizations.length === 0 ||
                  (useApiKey && !apiKeyInput.trim()) ||
                  (uploadType === 'universe' && selectedOrganization === FASTCUBE_ORG)
                }
                onFileSelect={handleFileUpload}
              />
              {uploading && (
                <p className="text-sm text-blue-600">Uploading file...</p>
              )}
              {uploadType === 'universe' && selectedOrganization === FASTCUBE_ORG && (
                <p className="text-sm text-amber-600">
                  Universe upload is not available for FastCube (no universe table).
                </p>
              )}
              {!organizationsLoading && organizations.length === 0 && (
                <p className="text-sm text-amber-600">
                  File upload is disabled because you are not associated with any organizations.
                </p>
              )}
              {!organizationsLoading &&
                organizations.length > 0 &&
                !(uploadType === 'universe' && selectedOrganization === FASTCUBE_ORG) && (
                <p className="text-sm text-gray-500">
                  {uploadType === 'universe'
                    ? 'Choose a CSV, set Email or md5 column and Email format, then Upload. Values are validated in Databricks (md5: 32 hex chars; email: addresses hashed to MD5). Limit is 2 GB — for larger files use S3.'
                    : uploadType === 'fast_id'
                      ? 'Choose a CSV, set Email or md5 column and Email format, then Upload. Same validation as Universe. Limit is 2 GB — for larger files use S3.'
                      : 'Select a bulk file to upload (CSV or JSON). Limit is 2 GB — for larger files use Bulk input → S3.'}
                </p>
              )}
              {uploadType === 'bulk' && (
                <>
                  {bulkFileParsing && <p className="text-sm text-blue-600">Reading file...</p>}
                  {selectedBulkFile && (
                    <p className="text-sm text-gray-600">
                      Selected: <span className="font-medium">{selectedBulkFile.name}</span>
                    </p>
                  )}
                </>
              )}
              {uploadType === 'universe' && universeInputMode === 'file' && selectedUniverseFile && (
                <div className="space-y-4 border-t border-gray-200 pt-4 mt-4">
                  <p className="text-sm text-gray-600">
                    Selected: <span className="font-medium">{selectedUniverseFile.name}</span>
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="universeEmailMd5Column">Email or md5 column</Label>
                      <FunctionalSelect
                        id="universeEmailMd5Column"
                        value={universeEmailMd5Column}
                        onValueChange={setUniverseEmailMd5Column}
                        placeholder="Column"
                        className="w-full"
                        disabled={uploading}
                      >
                        {UNIVERSE_COLUMN_OPTIONS.map((n) => (
                          <SelectItem key={n} value={n}>
                            {n}
                          </SelectItem>
                        ))}
                      </FunctionalSelect>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="universeEmailFormat">Email format</Label>
                      <FunctionalSelect
                        id="universeEmailFormat"
                        value={universeEmailFormat}
                        onValueChange={setUniverseEmailFormat}
                        placeholder="Format"
                        className="w-full"
                        disabled={uploading}
                      >
                        <SelectItem value="md5">md5</SelectItem>
                        <SelectItem value="email">email</SelectItem>
                      </FunctionalSelect>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={handleUniverseUpload}
                      disabled={
                        uploading ||
                        !selectedOrganization ||
                        organizationsLoading ||
                        organizations.length === 0 ||
                        (useApiKey && !apiKeyInput.trim()) ||
                        selectedOrganization === FASTCUBE_ORG
                      }
                      className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {uploading ? 'Uploading…' : 'Upload'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        clearUniverseFileInput();
                      }}
                      disabled={uploading}
                      className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {uploadType === 'fast_id' && fastIdInputMode === 'file' && selectedFastIdFile && (
                <div className="space-y-4 border-t border-gray-200 pt-4 mt-4">
                  <p className="text-sm text-gray-600">
                    Selected: <span className="font-medium">{selectedFastIdFile.name}</span>
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="fastIdEmailMd5Column">Email or md5 column</Label>
                      <FunctionalSelect
                        id="fastIdEmailMd5Column"
                        value={fastIdEmailMd5Column}
                        onValueChange={setFastIdEmailMd5Column}
                        placeholder="Column"
                        className="w-full"
                        disabled={uploading}
                      >
                        {UNIVERSE_COLUMN_OPTIONS.map((n) => (
                          <SelectItem key={n} value={n}>
                            {n}
                          </SelectItem>
                        ))}
                      </FunctionalSelect>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="fastIdEmailFormat">Email format</Label>
                      <FunctionalSelect
                        id="fastIdEmailFormat"
                        value={fastIdEmailFormat}
                        onValueChange={setFastIdEmailFormat}
                        placeholder="Format"
                        className="w-full"
                        disabled={uploading}
                      >
                        <SelectItem value="md5">md5</SelectItem>
                        <SelectItem value="email">email</SelectItem>
                      </FunctionalSelect>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={handleFastIdUpload}
                      disabled={
                        uploading ||
                        !selectedOrganization ||
                        organizationsLoading ||
                        organizations.length === 0 ||
                        (useApiKey && !apiKeyInput.trim())
                      }
                      className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {uploading ? 'Uploading…' : 'Upload'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        clearFastIdFileInput();
                      }}
                      disabled={uploading}
                      className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {uploadType === 'bulk' &&
            ((bulkInputMode === 'file' && selectedBulkFile && bulkFileColumns.length > 0) ||
              bulkInputMode === 's3') && (
            <div className="space-y-4 border-t pt-4">
              {bulkInputMode === 's3' && (
                <div className="space-y-2">
                  <Label htmlFor="bulkS3Path">S3 Path</Label>
                  <input
                    id="bulkS3Path"
                    type="text"
                    value={bulkS3Path}
                    onChange={(e) => setBulkS3Path(e.target.value)}
                    placeholder="s3://your-bucket/path/to/file.csv"
                    disabled={
                      uploading ||
                      !selectedOrganization ||
                      organizationsLoading ||
                      organizations.length === 0 ||
                      (useApiKey && !apiKeyInput.trim())
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#0062FB] focus:border-[#0062FB] disabled:opacity-50 disabled:bg-gray-50 text-sm font-mono"
                    autoComplete="off"
                  />
                  <p className="text-sm text-gray-500">
                    Use a CSV or JSON file already in S3 (same job as file upload; copy into the uploads bucket may run in the background). Enter column names exactly as in the file header.
                  </p>
                </div>
              )}
              <div className="space-y-3">
                <Label className="text-gray-700">File Information</Label>
                <div className="grid gap-x-4 gap-y-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {BULK_METADATA_FIELDS.map((key) => (
                    <div key={key} className="space-y-1">
                      <Label htmlFor={`bulk-meta-${key}`} className="text-sm text-gray-600">
                        {BULK_METADATA_FIELD_LABELS[key] ?? key}
                      </Label>
                      <input
                        id={`bulk-meta-${key}`}
                        type="text"
                        value={bulkMetadata[key] ?? ''}
                        onChange={(e) => setBulkMetadata((prev) => ({ ...prev, [key]: e.target.value }))}
                        disabled={uploading}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-[#0062FB] focus:border-[#0062FB] disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-3 pt-4 border-t">
                <Label>Map file columns to schema</Label>
                <p className="text-sm text-gray-500">
                  {bulkInputMode === 's3'
                    ? 'Check each field you want to map, then type the exact CSV/JSON column name. Email, Opt-in IP address, and Opt-in date are required and cannot be unchecked.'
                    : 'Check each field you want to map, then choose the file column. Email, Opt-in IP address, and Opt-in date are required and cannot be unchecked. First row is validated below.'}
                </p>
                {bulkInputMode === 'file' &&
                  !bulkHasHeader &&
                  bulkFirstLinePreview != null &&
                  bulkFirstLinePreview.length > 0 && (
                    <p className="text-sm text-gray-800 font-mono break-all whitespace-pre-wrap">
                      {bulkFirstLinePreview.map((cell, i) => (
                        <React.Fragment key={i}>
                          {i > 0 ? ' | ' : null}
                          <span className="font-bold">c{i}:</span>
                          {cell ? ` ${cell}` : ''}
                        </React.Fragment>
                      ))}
                    </p>
                  )}
                <div className="grid gap-x-4 gap-y-3 grid-cols-[auto_minmax(0,max-content)_minmax(0,1fr)_minmax(0,1.5fr)] items-center">
                  {BULK_SCHEMA_FIELDS.map((field) => {
                    const fieldError = getBulkValidationErrors()[field];
                    const locked = BULK_ALWAYS_ENABLED_FIELDS.has(field);
                    const enabled = locked || bulkFieldEnabled[field] === true;
                    return (
                      <React.Fragment key={field}>
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            id={`bulk-enable-${field}`}
                            checked={enabled}
                            onChange={(e) => {
                              if (BULK_ALWAYS_ENABLED_FIELDS.has(field)) return;
                              const checked = e.target.checked;
                              setBulkFieldEnabled((prev) => ({ ...prev, [field]: checked }));
                              if (!checked) setBulkColumnMapping((prev) => ({ ...prev, [field]: '' }));
                            }}
                            disabled={uploading || locked}
                            className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300 rounded"
                          />
                        </div>
                        <Label
                          htmlFor={`bulk-enable-${field}`}
                          className={`text-sm ${locked ? 'cursor-default' : 'cursor-pointer'} ${enabled ? 'text-gray-700' : 'text-gray-400'}`}
                        >
                          <span className="font-bold">{BULK_SCHEMA_FIELD_LABELS[field] ?? field}</span>
                        </Label>
                        {bulkInputMode === 's3' ? (
                          <input
                            id={`bulk-map-s3-${field}`}
                            type="text"
                            value={bulkColumnMapping[field] ?? ''}
                            onChange={(e) =>
                              setBulkColumnMapping((prev) => ({ ...prev, [field]: e.target.value }))
                            }
                            placeholder="Column name in file"
                            disabled={uploading || !enabled}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-[#0062FB] focus:border-[#0062FB] disabled:opacity-50 disabled:cursor-not-allowed ${enabled ? '' : 'opacity-60'}`}
                            autoComplete="off"
                          />
                        ) : (
                          <FunctionalSelect
                            id={`bulk-map-${field}`}
                            value={bulkColumnMapping[field] ?? ''}
                            onValueChange={(value) => {
                              setBulkColumnMapping((prev) => ({ ...prev, [field]: value }));
                            }}
                            placeholder="Select column"
                            className={`w-full ${enabled ? '' : 'opacity-60'}`}
                            disabled={uploading || !enabled}
                          >
                            {[...bulkFileColumns].sort((a, b) => a.localeCompare(b)).map((col) => (
                              <SelectItem key={col} value={col}>
                                {col}
                              </SelectItem>
                            ))}
                          </FunctionalSelect>
                        )}
                        <div className="min-h-[1.25rem]">
                          {fieldError && (
                            <p className="text-xs text-red-600">{fieldError}</p>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                {bulkInputMode === 'file' ? (
                  <button
                    type="button"
                    onClick={() => handleBulkUpload(selectedBulkFile, bulkColumnMapping)}
                    disabled={uploading || Object.keys(getBulkValidationErrors()).length > 0}
                    className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleBulkS3Submit}
                    disabled={
                      uploading ||
                      !(bulkS3Path || '').trim() ||
                      !selectedOrganization ||
                      organizationsLoading ||
                      organizations.length === 0 ||
                      (useApiKey && !apiKeyInput.trim()) ||
                      ['email', 'optin_ip_address', 'optin_date'].some(
                        (f) => !String(bulkColumnMapping[f] || '').trim()
                      ) ||
                      BULK_METADATA_FIELDS.some((key) => !String(bulkMetadata[key] || '').trim())
                    }
                    className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {uploading ? 'Submitting…' : 'Submit'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (bulkInputMode === 's3') {
                      setBulkS3Path('');
                      setBulkColumnMapping(BULK_SCHEMA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
                      setBulkFieldEnabled(defaultBulkFieldEnabled());
                      setBulkMetadata(BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
                      return;
                    }
                    setSelectedBulkFile(null);
                    setBulkFileColumns([]);
                    setBulkColumnMapping({});
                    setBulkFieldEnabled({});
                    setBulkMetadata(BULK_METADATA_FIELDS.reduce((acc, f) => ({ ...acc, [f]: '' }), {}));
                    setBulkFirstRow(null);
                    setBulkFirstLinePreview(null);
                    const fileInput = document.getElementById('uploadFile');
                    if (fileInput) fileInput.value = '';
                  }}
                  disabled={uploading}
                  className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {uploadType === 'universe' && universeInputMode === 's3' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="universeS3Path">S3 Path</Label>
                <input
                  id="universeS3Path"
                  type="text"
                  value={universeS3Path}
                  onChange={(e) => setUniverseS3Path(e.target.value)}
                  placeholder="s3://<bucket>/<prefix>"
                  disabled={uploading || !selectedOrganization || organizationsLoading || organizations.length === 0 || (useApiKey && !apiKeyInput.trim()) || selectedOrganization === FASTCUBE_ORG}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#0062FB] focus:border-[#0062FB] disabled:opacity-50 disabled:bg-gray-50 text-sm font-mono"
                  autoComplete="off"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="universeS3EmailMd5Column">Email or md5 column</Label>
                  <FunctionalSelect
                    id="universeS3EmailMd5Column"
                    value={universeEmailMd5Column}
                    onValueChange={setUniverseEmailMd5Column}
                    placeholder="Column"
                    className="w-full"
                    disabled={uploading}
                  >
                    {UNIVERSE_COLUMN_OPTIONS.map((n) => (
                      <SelectItem key={n} value={n}>
                        {n}
                      </SelectItem>
                    ))}
                  </FunctionalSelect>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="universeS3EmailFormat">Email format</Label>
                  <FunctionalSelect
                    id="universeS3EmailFormat"
                    value={universeEmailFormat}
                    onValueChange={setUniverseEmailFormat}
                    placeholder="Format"
                    className="w-full"
                    disabled={uploading}
                  >
                    <SelectItem value="md5">md5</SelectItem>
                    <SelectItem value="email">email</SelectItem>
                  </FunctionalSelect>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Use an existing CSV in S3 that Databricks can read (recommended for files over 2 GB). Column and format match file upload and are passed to validation in Databricks.
              </p>
              <button
                type="button"
                onClick={handleUniverseS3Submit}
                disabled={uploading || !(universeS3Path || '').trim() || !selectedOrganization || organizationsLoading || organizations.length === 0 || (useApiKey && !apiKeyInput.trim()) || selectedOrganization === FASTCUBE_ORG}
                className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Submit
              </button>
            </div>
          )}

          {uploadType === 'fast_id' && fastIdInputMode === 's3' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fastIdS3Path">S3 Path</Label>
                <input
                  id="fastIdS3Path"
                  type="text"
                  value={fastIdS3Path}
                  onChange={(e) => setFastIdS3Path(e.target.value)}
                  placeholder="s3://<bucket>/<prefix>"
                  disabled={uploading || !selectedOrganization || organizationsLoading || organizations.length === 0 || (useApiKey && !apiKeyInput.trim())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#0062FB] focus:border-[#0062FB] disabled:opacity-50 disabled:bg-gray-50 text-sm font-mono"
                  autoComplete="off"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="fastIdS3EmailMd5Column">Email or md5 column</Label>
                  <FunctionalSelect
                    id="fastIdS3EmailMd5Column"
                    value={fastIdEmailMd5Column}
                    onValueChange={setFastIdEmailMd5Column}
                    placeholder="Column"
                    className="w-full"
                    disabled={uploading}
                  >
                    {UNIVERSE_COLUMN_OPTIONS.map((n) => (
                      <SelectItem key={n} value={n}>
                        {n}
                      </SelectItem>
                    ))}
                  </FunctionalSelect>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fastIdS3EmailFormat">Email format</Label>
                  <FunctionalSelect
                    id="fastIdS3EmailFormat"
                    value={fastIdEmailFormat}
                    onValueChange={setFastIdEmailFormat}
                    placeholder="Format"
                    className="w-full"
                    disabled={uploading}
                  >
                    <SelectItem value="md5">md5</SelectItem>
                    <SelectItem value="email">email</SelectItem>
                  </FunctionalSelect>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Use an existing CSV in S3 that Databricks can read (recommended for files over 2 GB). Column and format match file upload and are passed to validation in Databricks.
              </p>
              <button
                type="button"
                onClick={handleFastIdS3Submit}
                disabled={uploading || !(fastIdS3Path || '').trim() || !selectedOrganization || organizationsLoading || organizations.length === 0 || (useApiKey && !apiKeyInput.trim())}
                className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Submit
              </button>
            </div>
          )}

          {uploadType === 'universe' && universeInputMode === 'text' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Text is uploaded as one value per line (single column). Same format option as file and S3 upload (Databricks validate_universe).
              </p>
              <div className="space-y-2">
                <Label htmlFor="universeHashes">
                  {universeEmailFormat === 'email'
                    ? 'Paste comma-separated email addresses'
                    : 'Paste comma-separated MD5 hashes'}
                </Label>
                <textarea
                  id="universeHashes"
                  value={universeHashesText}
                  onChange={(e) => setUniverseHashesText(e.target.value)}
                  placeholder={
                    universeEmailFormat === 'email'
                      ? 'e.g. user@example.com, other@yahoo.com'
                      : 'e.g. abc123..., def456... (32 hex chars each)'
                  }
                  rows={5}
                  disabled={uploading || !selectedOrganization || organizationsLoading || organizations.length === 0 || (useApiKey && !apiKeyInput.trim()) || selectedOrganization === FASTCUBE_ORG}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#0062FB] focus:border-[#0062FB] disabled:opacity-50 disabled:bg-gray-50 text-sm font-mono"
                />
              </div>
              <div className="space-y-2 max-w-sm">
                <Label htmlFor="universeTextEmailFormat">Email format</Label>
                <FunctionalSelect
                  id="universeTextEmailFormat"
                  value={universeEmailFormat}
                  onValueChange={setUniverseEmailFormat}
                  placeholder="Format"
                  className="w-full"
                  disabled={uploading}
                >
                  <SelectItem value="md5">md5</SelectItem>
                  <SelectItem value="email">email</SelectItem>
                </FunctionalSelect>
              </div>
              <button
                type="button"
                onClick={handleUniverseHashesSubmit}
                disabled={uploading || !(universeHashesText || '').trim() || !selectedOrganization || organizationsLoading || organizations.length === 0 || (useApiKey && !apiKeyInput.trim()) || selectedOrganization === FASTCUBE_ORG}
                className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Submit
              </button>
            </div>
          )}

          {uploadType === 'fast_id' && fastIdInputMode === 'text' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Text is uploaded as one value per line (single column). Same format option as file and S3 upload (Databricks validate_universe).
              </p>
              <div className="space-y-2">
                <Label htmlFor="fastIdHashes">
                  {fastIdEmailFormat === 'email'
                    ? 'Paste comma-separated email addresses'
                    : 'Paste comma-separated MD5 hashes'}
                </Label>
                <textarea
                  id="fastIdHashes"
                  value={fastIdHashesText}
                  onChange={(e) => setFastIdHashesText(e.target.value)}
                  placeholder={
                    fastIdEmailFormat === 'email'
                      ? 'e.g. user@example.com, other@yahoo.com'
                      : 'e.g. abc123..., def456... (32 hex chars each)'
                  }
                  rows={5}
                  disabled={uploading || !selectedOrganization || organizationsLoading || organizations.length === 0 || (useApiKey && !apiKeyInput.trim())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#0062FB] focus:border-[#0062FB] disabled:opacity-50 disabled:bg-gray-50 text-sm font-mono"
                />
              </div>
              <div className="space-y-2 max-w-sm">
                <Label htmlFor="fastIdTextEmailFormat">Email format</Label>
                <FunctionalSelect
                  id="fastIdTextEmailFormat"
                  value={fastIdEmailFormat}
                  onValueChange={setFastIdEmailFormat}
                  placeholder="Format"
                  className="w-full"
                  disabled={uploading}
                >
                  <SelectItem value="md5">md5</SelectItem>
                  <SelectItem value="email">email</SelectItem>
                </FunctionalSelect>
              </div>
              <button
                type="button"
                onClick={handleFastIdHashesSubmit}
                disabled={uploading || !(fastIdHashesText || '').trim() || !selectedOrganization || organizationsLoading || organizations.length === 0 || (useApiKey && !apiKeyInput.trim())}
                className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Submit
              </button>
            </div>
          )}

          {/* Display Query (curl command) */}
          {uploadResult && useApiKey && apiKeyInput.trim() && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Query:</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 overflow-auto">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                  {(() => {
                    const baseUrl = window.location.origin;
                    const apiKeyValue = '<API_KEY_TOKEN>';
                    const fileInputId = uploadType === 'fast_id' ? 'uploadFileFastId' : 'uploadFile';
                    const fileName =
                      document.getElementById(fileInputId)?.files?.[0]?.name || '<filename>';
                    const isLocalhost = baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1');
                    const isHttps = baseUrl.startsWith('https://');
                    const kFlag = isLocalhost && isHttps ? ' -k' : '';
                    const h = `  -H "Authorization: apikey ${apiKeyValue}"`;
                    const s3PostNote =
                      '# 2) POST to post_url from step 1: include every form_fields key=value, then multipart part "file" (last) with the bytes.';

                    if (uploadType === 'fast_id' && fastIdInputMode === 's3') {
                      return `curl -X POST "${baseUrl}/api/v2/upload_fast_id_s3"${kFlag} \\\n${h} \\\n  -F "s3_path=s3://your-bucket/your-prefix" \\\n  -F "organization=${selectedOrganization}" \\\n  -F "email_md5_column=${fastIdEmailMd5Column}" \\\n  -F "email_format=${fastIdEmailFormat}"`;
                    }
                    if (uploadType === 'fast_id' && fastIdInputMode === 'text') {
                      return `curl -X POST "${baseUrl}/api/v2/upload_fast_id_hashes"${kFlag} \\\n${h} \\\n  -F "hashes=<comma-separated-md5-or-emails>" \\\n  -F "organization=${selectedOrganization}" \\\n  -F "email_format=${fastIdEmailFormat}"`;
                    }
                    if (uploadType === 'fast_id' && fastIdInputMode === 'file') {
                      return [
                        '# 1) Presigned POST policy',
                        `curl -s -X POST "${baseUrl}/api/v2/upload_fast_id_presign"${kFlag} \\`,
                        `${h} \\`,
                        `  -F "organization=${selectedOrganization}" \\`,
                        `  -F "file_name=${fileName}"`,
                        s3PostNote,
                        '# 3) Start Fast-ID job',
                        `curl -X POST "${baseUrl}/api/v2/upload_fast_id_s3"${kFlag} \\`,
                        `${h} \\`,
                        '  -F "s3_path=<s3_path from step 1 response>" \\',
                        `  -F "organization=${selectedOrganization}" \\`,
                        `  -F "email_md5_column=${fastIdEmailMd5Column}" \\`,
                        `  -F "email_format=${fastIdEmailFormat}"`,
                      ].join('\n');
                    }
                    if (uploadType === 'universe' && universeInputMode === 's3') {
                      return `curl -X POST "${baseUrl}/api/v2/upload_universe_s3"${kFlag} \\\n${h} \\\n  -F "s3_path=s3://your-bucket/your-prefix" \\\n  -F "organization=${selectedOrganization}" \\\n  -F "email_md5_column=${universeEmailMd5Column}" \\\n  -F "email_format=${universeEmailFormat}" \\\n  -F "replace=${universeReplace}"`;
                    }
                    if (uploadType === 'universe' && universeInputMode === 'text') {
                      return `curl -X POST "${baseUrl}/api/v2/upload_universe_hashes"${kFlag} \\\n${h} \\\n  -F "hashes=<comma-separated-md5-or-emails>" \\\n  -F "organization=${selectedOrganization}" \\\n  -F "email_format=${universeEmailFormat}" \\\n  -F "replace=${universeReplace}"`;
                    }
                    if (uploadType === 'universe' && universeInputMode === 'file') {
                      return [
                        '# 1) Presigned POST policy',
                        `curl -s -X POST "${baseUrl}/api/v2/upload_universe_presign"${kFlag} \\`,
                        `${h} \\`,
                        `  -F "organization=${selectedOrganization}" \\`,
                        `  -F "file_name=${fileName}"`,
                        s3PostNote,
                        '# 3) Start universe job',
                        `curl -X POST "${baseUrl}/api/v2/upload_universe_s3"${kFlag} \\`,
                        `${h} \\`,
                        '  -F "s3_path=<s3_path from step 1 response>" \\',
                        `  -F "organization=${selectedOrganization}" \\`,
                        `  -F "email_md5_column=${universeEmailMd5Column}" \\`,
                        `  -F "email_format=${universeEmailFormat}" \\`,
                        `  -F "replace=${universeReplace}"`,
                      ].join('\n');
                    }
                    if (uploadType === 'bulk' && bulkInputMode === 's3') {
                      return `curl -X POST "${baseUrl}/api/v2/upload_bulk_s3"${kFlag} \\\n${h} \\\n  -F "organization=${selectedOrganization}" \\\n  -F "s3_path=s3://your-bucket/path/file.csv" \\\n  -F "has_header=${bulkHasHeader}" \\\n  -F "email_field=email" \\\n  -F "optin_ip_address_field=ip" \\\n  -F "optin_site_field=site" \\\n  -F "optin_date_field=signed_up" \\\n  -F "supplier_name=..." \\\n  -F "purchase_date=..." \\\n  -F "quality=..." \\\n  -F "domains=..." \\\n  -F "cost=..." \\\n  -F "agreement=..."`;
                    }
                    if (uploadType === 'bulk' && bulkInputMode === 'file') {
                      return [
                        '# 1) Presigned POST policy (FastCube + admin only)',
                        `curl -s -X POST "${baseUrl}/api/v2/upload_bulk_presign"${kFlag} \\`,
                        `${h} \\`,
                        `  -F "organization=${selectedOrganization}" \\`,
                        `  -F "file_name=${fileName}"`,
                        s3PostNote,
                        '# 3) Start bulk job (mappings and file information: same as the File Upload form)',
                        `curl -X POST "${baseUrl}/api/v2/upload_bulk_s3"${kFlag} \\\n${h} \\\n  -F "s3_path=<s3_path from step 1 response>" \\\n  -F "organization=${selectedOrganization}" \\\n  -F "has_header=${bulkHasHeader}" \\\n  -F "email_field=email" \\\n  -F "optin_ip_address_field=ip" \\\n  -F "optin_site_field=site" \\\n  -F "optin_date_field=signed_up" \\\n  -F "supplier_name=..." \\\n  -F "purchase_date=..." \\\n  -F "quality=..." \\\n  -F "domains=..." \\\n  -F "cost=..." \\\n  -F "agreement=..."`,
                      ].join('\n');
                    }
                    return '# Select an upload type and input mode to see a curl example.';
                  })()}
                </pre>
              </div>
            </div>
          )}

          {/* Display JSON Result - only when using API Key */}
          {uploadResult && useApiKey && apiKeyInput.trim() && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Result:</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 overflow-auto">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                  {JSON.stringify(uploadResult, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload History: user's universe (file_upload) and/or bulk (bulk_file_upload) rows across all orgs */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <CardTitle className="mb-0">Upload History</CardTitle>
            <div className="space-y-2 w-full sm:w-64 sm:shrink-0">
              <Label htmlFor="uploadHistoryTypeFilter">Upload type</Label>
              <FunctionalSelect
                id="uploadHistoryTypeFilter"
                value={uploadHistoryTypeFilter}
                onValueChange={(value) => {
                  setUploadHistoryTypeFilter(value);
                  setExpandedHistoryId(null);
                }}
                className="w-full"
              >
                <SelectItem value={UPLOAD_HISTORY_TYPE_FILTER.all}>All</SelectItem>
                <SelectItem value={UPLOAD_HISTORY_TYPE_FILTER.universe}>{UPLOAD_TYPES.universe}</SelectItem>
                <SelectItem value={UPLOAD_HISTORY_TYPE_FILTER.fast_id}>{UPLOAD_TYPES.fast_id}</SelectItem>
                {selectedOrganization === FASTCUBE_ORG && (
                  <SelectItem value={UPLOAD_HISTORY_TYPE_FILTER.bulk}>{UPLOAD_TYPES.bulk}</SelectItem>
                )}
              </FunctionalSelect>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingUploadHistory ? (
            <p className="text-sm text-gray-500">Loading upload history...</p>
          ) : uploadHistory.length === 0 ? (
            <p className="text-sm text-gray-500">No uploads yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200 rounded-md">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium text-gray-700 border-b w-9" aria-label="Expand" />
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Upload time</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">File name</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Organization</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Type</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Status</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b min-w-[11rem]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadHistory.map((row) => {
                    const rowKey = getHistoryRowKey(row);
                    const showProcessingReportButton =
                      (row.processing_status === 'validated' ||
                        row.processing_status === 'complete' ||
                        row.processing_status === 'error') &&
                      Boolean(row.execution_id);
                    const showFastIdReportButton =
                      row.processing_status === 'complete' && Boolean(row.execution_id);
                    return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setExpandedHistoryId((prev) => (prev === rowKey ? null : rowKey))}
                      >
                        <td className="px-2 py-2 text-gray-500" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => setExpandedHistoryId((prev) => (prev === rowKey ? null : rowKey))}
                            className="p-0.5 rounded hover:bg-gray-200"
                            aria-expanded={expandedHistoryId === rowKey}
                          >
                            {expandedHistoryId === rowKey ? (
                              <ChevronDown size={18} />
                            ) : (
                              <ChevronRight size={18} />
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                          {row.upload_time ? new Date(row.upload_time).toLocaleString() : '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-800">{row.file_name || '—'}</td>
                        <td className="px-3 py-2 text-gray-800">{row.organization_name || '—'}</td>
                        <td className="px-3 py-2 text-gray-800">{formatUploadHistoryType(row.type)}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            row.processing_status === 'started' || row.processing_status === 'validated'
                              ? 'bg-green-100 text-green-800' :
                            row.processing_status === 'complete' ? 'bg-blue-100 text-blue-800' :
                            row.processing_status === 'error' ? 'bg-red-100 text-red-800' :
                            row.processing_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {row.processing_status || '—'}
                          </span>
                        </td>
                        <td
                          className="px-3 py-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.type === 'bulk' ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setBulkDetailsRow(row)}
                              >
                                Details
                              </Button>
                              {showProcessingReportButton && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openProcessingReport(row.execution_id)}
                                >
                                  Report
                                </Button>
                              )}
                            </div>
                          ) : row.type === 'universe' ? (
                            showProcessingReportButton ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => openProcessingReport(row.execution_id)}
                              >
                                Report
                              </Button>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )
                          ) : row.type === 'fast_id' ? (
                            showFastIdReportButton ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  openProcessingReport(row.execution_id, { variant: 'fast_id' })
                                }
                              >
                                Report
                              </Button>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )
                          ) : (
                            <span className="text-gray-400 text-sm">—</span>
                          )}
                        </td>
                      </tr>
                      {expandedHistoryId === rowKey && (
                        <tr key={`${rowKey}-expanded`} className="border-b border-gray-100 bg-gray-50">
                          <td colSpan={7} className="px-3 py-2">
                            <div className="text-xs text-gray-600 space-y-1">
                              {selectedOrganization === FASTCUBE_ORG && (
                                <div>
                                  <span className="font-medium text-gray-700">S3 URL:</span>{' '}
                                  <code className="break-all bg-gray-100 px-2 py-1 rounded" title={row.s3_url}>
                                    {row.s3_url || '—'}
                                  </code>
                                </div>
                              )}
                              {row.execution_id && (
                                <div>
                                  <span className="font-medium text-gray-700">Execution ID:</span>{' '}
                                  <code className="break-all bg-gray-100 px-2 py-1 rounded" title={row.execution_id}>
                                    {row.execution_id}
                                  </code>
                                </div>
                              )}
                              {row.upload_id != null && (
                                <div>
                                  <span className="font-medium text-gray-700">Upload ID:</span>{' '}
                                  <code className="break-all bg-gray-100 px-2 py-1 rounded" title={String(row.upload_id)}>
                                    {row.upload_id}
                                  </code>
                                </div>
                              )}
                              {row.file_md5 ? (
                                <div>
                                  <span className="font-medium text-gray-700">File MD5:</span>{' '}
                                  <code
                                    className="break-all bg-gray-100 px-2 py-1 rounded"
                                    title={row.file_md5}
                                  >
                                    {row.file_md5}
                                  </code>
                                </div>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {bulkDetailsRow && (
      <Modal
        isOpen
        onClose={() => setBulkDetailsRow(null)}
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
            <CardHeader className="pr-10">
              <CardTitle>Bulk upload details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-gray-800 space-y-5">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">File information</h4>
                  <div className="space-y-1.5">
                    {BULK_METADATA_FIELDS.map((key) => (
                      <div key={key}>
                        <span className="font-medium text-gray-800">{BULK_METADATA_FIELD_LABELS[key]}:</span>{' '}
                        <span>{formatBulkDetailValue(bulkDetailsRow[key])}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Column mapping</h4>
                  <div className="space-y-1.5">
                    {BULK_COLUMN_DETAIL_KEYS.map((key) => (
                      <div key={key}>
                        <span className="font-medium text-gray-800">{BULK_EXPANDED_FIELD_LABELS[key]}:</span>{' '}
                        <span>{formatBulkDetailValue(bulkDetailsRow[key])}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
      </Modal>
      )}

      <Modal
        isOpen={showUniverseReplaceConfirm}
        onClose={closeUniverseReplaceConfirm}
        className="w-full max-w-md mx-4"
      >
            <CardHeader className="pr-10">
              <CardTitle>Confirm</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Are you sure you want to drop and reload the universe table for{' '}
                <span className="font-medium">{selectedOrganization}</span>?
              </p>
              <div className="flex justify-end">
                <Button type="button" variant="outline" onClick={confirmUniverseReplace}>
                  Continue
                </Button>
              </div>
            </CardContent>
      </Modal>

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Error"
        message={errorMessage}
      />
    </div>
  );
};

export default FileUpload;

